import React, { useMemo, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';

// ─── Address validity ─────────────────────────────────────
// Travel rows carry placeholder endpoints — a hotel row with no address saved
// is literally just the concept "Hotel", which Google Maps can't route to.
// Only real addresses become stops; everything else is reported as skipped so
// the day's driving order stays honest rather than silently short.
const PLACEHOLDER = /^(hotel|motel|home|home ?base|lodging|camp|tbd|tba|n\/?a|none|unknown|-+|\?+)$/i;

export const isMappableAddress = (addr) => {
  const s = (addr || '').trim();
  if (s.length < 4) return false;
  return !PLACEHOLDER.test(s);
};

// ─── Map providers ────────────────────────────────────────
// Google's URL API takes an origin, a destination and up to 9 intermediate
// waypoints. Apple's Maps URL scheme has no waypoint parameter at all — it is
// strictly point-to-point — so an Apple route is expressed as consecutive
// two-stop legs. Both cases fall out of the same overlapping-leg split, just
// with a different cap, and neither drops a stop.
const GOOGLE_MAX_WAYPOINTS = 9;
export const MAX_STOPS_PER_LEG = GOOGLE_MAX_WAYPOINTS + 2;
const APPLE_MAX_STOPS_PER_LEG = 2;

export const PROVIDERS = {
  google: { label: 'Google Maps', maxStopsPerLeg: MAX_STOPS_PER_LEG },
  apple: { label: 'Apple Maps', maxStopsPerLeg: APPLE_MAX_STOPS_PER_LEG },
};

const PROVIDER_KEY = 'bvm.maps.provider.v1';

// Default to Apple on Apple hardware, otherwise Google — then honour whatever
// the user last picked, for every day map from then on.
const guessProvider = () => {
  if (typeof navigator === 'undefined') return 'google';
  return /iPhone|iPad|iPod|Macintosh/.test(navigator.userAgent) ? 'apple' : 'google';
};

export const getPreferredProvider = () => {
  try {
    const stored = window.localStorage.getItem(PROVIDER_KEY);
    if (stored && PROVIDERS[stored]) return stored;
  } catch {
    // storage unavailable — fall through to the platform guess
  }
  return guessProvider();
};

export const setPreferredProvider = (provider) => {
  try {
    window.localStorage.setItem(PROVIDER_KEY, provider);
  } catch {
    // Not persisted; the choice still applies for this modal.
  }
};

export const buildMapsUrl = (stops, provider = 'google') => {
  const pts = (stops || []).map((s) => encodeURIComponent(s.address));
  if (pts.length === 0) return null;
  const last = pts[pts.length - 1];

  if (provider === 'apple') {
    // dirflg=d selects driving directions.
    if (pts.length === 1) return `https://maps.apple.com/?daddr=${last}&dirflg=d`;
    return `https://maps.apple.com/?saddr=${pts[0]}&daddr=${last}&dirflg=d`;
  }

  if (pts.length === 1) {
    return `https://www.google.com/maps/dir/?api=1&destination=${last}&travelmode=driving`;
  }
  const waypoints = pts.slice(1, -1);
  let url = 'https://www.google.com/maps/dir/?api=1'
    + `&origin=${pts[0]}`
    + `&destination=${last}`
    + '&travelmode=driving';
  // %7C is the encoded "|" separator Google expects between waypoints.
  if (waypoints.length > 0) url += `&waypoints=${waypoints.join('%7C')}`;
  return url;
};

// Split a stop list into overlapping legs: each leg ends where the next begins,
// so following them back-to-back drives the full day in order.
export const chunkStops = (stops, maxPerLeg = MAX_STOPS_PER_LEG) => {
  if (stops.length <= maxPerLeg) return [stops];
  const legs = [];
  let i = 0;
  while (i < stops.length - 1) {
    legs.push(stops.slice(i, i + maxPerLeg));
    i += maxPerLeg - 1;
  }
  return legs;
};

const KIND_STYLE = {
  start: 'bg-green-600/20 text-green-400',
  job: 'bg-blue-500/20 text-blue-300',
  transit: 'bg-gray-500/20 text-gray-300',
  end: 'bg-green-600/20 text-green-400',
};

const DayRouteMapModal = ({ dayNumber, dateLabel, stops, excluded, showIntro = false, onClose }) => {
  const [provider, setProvider] = useState(getPreferredProvider);
  const [legIdx, setLegIdx] = useState(0);
  const [copied, setCopied] = useState(false);
  const [intro, setIntro] = useState(showIntro);

  const perLeg = PROVIDERS[provider].maxStopsPerLeg;
  const legs = useMemo(() => chunkStops(stops, perLeg), [stops, perLeg]);

  // Clamp: switching provider (or a realtime edit) can shrink the leg count.
  const activeIdx = Math.min(legIdx, legs.length - 1);
  const activeLeg = legs[activeIdx] || [];
  const url = buildMapsUrl(activeLeg, provider);
  // Index of this leg's first stop within the full day, for numbering.
  const legOffset = activeIdx * (perLeg - 1);

  const chooseProvider = (next) => {
    setProvider(next);
    setPreferredProvider(next); // remembered for every day map from here on
    setLegIdx(0);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center px-4 pt-12 overflow-y-auto">
      <div className="fixed inset-0 bg-gray-900/80" onClick={onClose} />
      <div className="relative bg-gray-700 rounded-lg shadow-xl max-w-2xl w-full p-6 mb-12">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-lg font-semibold text-white">
            Driving Route — Day {dayNumber}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-xl" aria-label="Close">&times;</button>
        </div>
        <p className="text-xs text-gray-400 mb-4">
          {dateLabel} — stops in route order, driving directions.
        </p>

        {/* Shown the first time, when the feature tour opens this modal. */}
        {intro && (
          <div className="mb-4 px-3 py-2.5 rounded bg-green-600/15 border border-green-600/50">
            <p className="text-xs font-semibold text-white mb-1">What this does</p>
            <p className="text-xs text-gray-300 leading-relaxed">
              It takes the day&rsquo;s stops in the order you have them — start point, each
              job, then where you finish — and builds driving directions. Open it on this
              computer, or scan the QR code to send the same route to your phone. Pick
              Google or Apple Maps at the bottom; that choice is remembered.
            </p>
            <div className="flex justify-end mt-2">
              <button
                onClick={() => setIntro(false)}
                className="px-3 py-1 text-[11px] font-medium bg-green-700 text-white rounded hover:bg-green-900 transition-colors"
              >
                Got it
              </button>
            </div>
          </div>
        )}

        {stops.length === 0 ? (
          <p className="text-sm text-gray-300 py-4">
            No mappable addresses on this day yet. Add a job location, or set an
            address on the start/end travel rows.
          </p>
        ) : (
          <>
            {legs.length > 1 && (
              <div className="mb-3 px-3 py-2 rounded bg-amber-400/10 border border-amber-400/30 text-xs text-amber-300">
                {provider === 'apple'
                  ? `Apple Maps links are point-to-point — they carry no intermediate stops. This day's ${stops.length} stops are split into ${legs.length} legs`
                  : `Google Maps allows ${GOOGLE_MAX_WAYPOINTS} stops between the start and end of a single link. This day has ${stops.length}, so it is split into ${legs.length} legs`}
                {' '}— each one picks up where the last ended.
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {legs.map((leg, i) => (
                    <button
                      key={i}
                      onClick={() => setLegIdx(i)}
                      className={`px-2 py-0.5 rounded font-medium transition-colors ${
                        i === activeIdx
                          ? 'bg-amber-400 text-gray-900'
                          : 'bg-gray-700 text-amber-300 hover:bg-gray-500/30'
                      }`}
                    >
                      Leg {i + 1}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-5">
              {/* Ordered stop list */}
              <ol className="flex-1 min-w-0 space-y-1.5">
                {activeLeg.map((stop, i) => (
                  <li key={`${stop.address}-${i}`} className="flex items-start gap-2 text-sm">
                    <span className="shrink-0 w-5 h-5 rounded-full bg-gray-900 text-gray-300 text-[11px] flex items-center justify-center mt-0.5">
                      {legOffset + i + 1}
                    </span>
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-white truncate">{stop.label}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wide font-medium ${KIND_STYLE[stop.kind] || KIND_STYLE.job}`}>
                          {stop.kind}
                        </span>
                      </div>
                      <div className="text-xs text-gray-400 break-words">{stop.address}</div>
                    </div>
                  </li>
                ))}
              </ol>

              {/* QR for handing the route to a phone */}
              <div className="shrink-0 flex flex-col items-center gap-2">
                <div className="bg-white p-2 rounded">
                  <QRCodeSVG value={url} size={148} level="L" />
                </div>
                <p className="text-[11px] text-gray-400 text-center max-w-[160px]">
                  Scan to open this {legs.length > 1 ? 'leg' : 'route'} in {PROVIDERS[provider].label} on a phone.
                </p>
              </div>
            </div>

            {excluded.length > 0 && (
              <div className="mt-4 pt-3 border-t border-gray-500/30">
                <p className="text-xs font-medium text-amber-300 mb-1">
                  Skipped ({excluded.length}) — no usable address:
                </p>
                <ul className="text-xs text-gray-400 space-y-0.5">
                  {excluded.map((e, i) => (
                    <li key={i}>{e.label} — {e.reason}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Provider choice lives down here, next to the action it affects. */}
            <div className="flex flex-wrap items-center justify-between gap-3 mt-5 pt-4 border-t border-gray-500/30">
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-300 font-medium">Open with:</span>
                <span className="inline-flex rounded-md overflow-hidden border border-gray-500/50">
                  {Object.entries(PROVIDERS).map(([key, { label }]) => (
                    <button
                      key={key}
                      onClick={() => chooseProvider(key)}
                      aria-pressed={provider === key}
                      className={`px-3 py-1 text-xs font-medium transition-colors ${
                        provider === key
                          ? 'bg-green-700 text-white'
                          : 'bg-gray-900 text-gray-300 hover:text-white hover:bg-gray-500/20'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </span>
              </div>
              <span className="text-[11px] text-gray-400">Remembered for next time</span>
            </div>

            <div className="flex flex-wrap items-center justify-end gap-2 mt-4">
              <button
                onClick={handleCopy}
                className="px-3 py-1.5 text-xs text-gray-300 hover:text-white hover:bg-gray-500/20 rounded transition-colors"
              >
                {copied ? 'Copied' : 'Copy link'}
              </button>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-1.5 bg-green-700 text-white text-sm font-medium rounded-md hover:bg-green-900 transition-colors"
              >
                Open in {PROVIDERS[provider].label}
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DayRouteMapModal;
